import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useTranslation } from 'react-i18next'
import { Language } from '@/types/multilingual'

export interface ProjectCategory {
  id: string
  title: string
  description: string
  icon: string | null
  sort_order: number
}

export interface Project {
  id: string
  category_id: string
  title: string
  subtitle: string | null
  description: string
  content: string | null
  image: string
  gallery: string[]
  status: 'completed' | 'in-progress' | 'planned'
  link: string | null
  tags: string[]
  features: string[]
  tech_stack: string[]
  metrics: Record<string, string | number>
  start_date: string | null
  end_date: string | null
  sort_order: number
  is_featured: boolean
  created_at: string
  updated_at: string
}

export function useCategories() {
  const { i18n } = useTranslation()
  const language = (i18n.language as Language) || 'zh'
  
  return useQuery({
    queryKey: ['project-categories', language],
    queryFn: async () => {
      // 获取分类
      const { data: categories, error: catError } = await supabase
        .from('project_categories')
        .select('*')
        .order('sort_order', { ascending: true })
      
      if (catError) throw catError
      if (!categories || categories.length === 0) return []
      
      // 获取对应语言的翻译
      const categoryIds = categories.map(c => c.id)
      const { data: translations, error: transError } = await supabase
        .from('category_translations')
        .select('*')
        .in('category_id', categoryIds)
        .eq('language', language)
      
      if (transError) throw transError
      
      // 创建翻译映射
      const translationMap = new Map(
        translations?.map(t => [t.category_id, t]) || []
      )
      
      // 合并数据
      return categories.map(category => {
        const trans = translationMap.get(category.id)
        return {
          id: category.id,
          icon: category.icon,
          sort_order: category.sort_order || 0,
          title: trans?.title || category.title,
          description: trans?.description || category.description
        } as ProjectCategory
      })
    }
  })
}

export function useProjects(categoryId?: string) {
  const { i18n } = useTranslation()
  const language = (i18n.language as Language) || 'zh'
  
  return useQuery({
    queryKey: ['projects', categoryId, language],
    queryFn: async () => {
      // 获取项目
      let projectQuery = supabase
        .from('multilingual_projects')
        .select('*')
        .order('sort_order', { ascending: true })
      
      if (categoryId) {
        projectQuery = projectQuery.eq('category_id', categoryId)
      }
      
      const { data: projects, error: projectError } = await projectQuery
      
      if (projectError) throw projectError
      if (!projects || projects.length === 0) return []
      
      // 获取对应语言的翻译
      const projectIds = projects.map(p => p.id)
      const { data: translations, error: transError } = await supabase
        .from('project_translations')
        .select('*')
        .in('project_id', projectIds)
        .eq('language', language)
      
      if (transError) throw transError
      
      // 创建翻译映射
      const translationMap = new Map(
        translations?.map(t => [t.project_id, t]) || []
      )
      
      // 合并数据
      return projects.map(project => {
        const trans = translationMap.get(project.id)
        return {
          id: project.id,
          category_id: project.category_id,
          image: project.image,
          status: project.status as 'completed' | 'in-progress' | 'planned',
          link: project.link,
          start_date: project.start_date,
          end_date: project.end_date,
          sort_order: project.sort_order || 0,
          is_featured: project.is_featured || false,
          created_at: project.created_at,
          updated_at: project.updated_at,
          gallery: Array.isArray(project.gallery) ? project.gallery : [],
          tags: Array.isArray(project.tags) ? project.tags : [],
          features: Array.isArray(project.features) ? project.features : [],
          tech_stack: Array.isArray(project.tech_stack) ? project.tech_stack : [],
          metrics: typeof project.metrics === 'object' && project.metrics ? project.metrics : {},
          title: trans?.title || project.id,
          subtitle: trans?.subtitle || null,
          description: trans?.description || '',
          content: trans?.content || null
        } as Project
      })
    }
  })
}

export function useFeaturedProjects() {
  const { i18n } = useTranslation()
  const language = (i18n.language as Language) || 'zh'
  
  return useQuery({
    queryKey: ['projects', 'featured', language],
    queryFn: async () => {
      // 获取精选项目
      const { data: projects, error: projectError } = await supabase
        .from('multilingual_projects')
        .select('*')
        .eq('is_featured', true)
        .order('sort_order', { ascending: true })
      
      if (projectError) throw projectError
      if (!projects || projects.length === 0) return []
      
      // 获取对应语言的翻译
      const projectIds = projects.map(p => p.id)
      const { data: translations, error: transError } = await supabase
        .from('project_translations')
        .select('*')
        .in('project_id', projectIds)
        .eq('language', language)
      
      if (transError) throw transError
      
      // 创建翻译映射
      const translationMap = new Map(
        translations?.map(t => [t.project_id, t]) || []
      )
      
      // 合并数据
      return projects.map(project => {
        const trans = translationMap.get(project.id)
        return {
          id: project.id,
          category_id: project.category_id,
          image: project.image,
          status: project.status as 'completed' | 'in-progress' | 'planned',
          link: project.link,
          start_date: project.start_date,
          end_date: project.end_date,
          sort_order: project.sort_order || 0,
          is_featured: project.is_featured || false,
          created_at: project.created_at,
          updated_at: project.updated_at,
          gallery: Array.isArray(project.gallery) ? project.gallery : [],
          tags: Array.isArray(project.tags) ? project.tags : [],
          features: Array.isArray(project.features) ? project.features : [],
          tech_stack: Array.isArray(project.tech_stack) ? project.tech_stack : [],
          metrics: typeof project.metrics === 'object' && project.metrics ? project.metrics : {},
          title: trans?.title || project.id,
          subtitle: trans?.subtitle || null,
          description: trans?.description || '',
          content: trans?.content || null
        } as Project
      })
    }
  })
}

export function useProject(projectId: string) {
  const { i18n } = useTranslation()
  const language = (i18n.language as Language) || 'zh'
  
  return useQuery({
    queryKey: ['project', projectId, language],
    queryFn: async () => {
      // 获取项目
      const { data: project, error: projectError } = await supabase
        .from('multilingual_projects')
        .select('*')
        .eq('id', projectId)
        .maybeSingle()
      
      if (projectError) throw projectError
      if (!project) return null
      
      // 获取翻译
      const { data: trans, error: transError } = await supabase
        .from('project_translations')
        .select('*')
        .eq('project_id', projectId)
        .eq('language', language)
        .maybeSingle()
      
      if (transError) throw transError
      
      return {
        id: project.id,
        category_id: project.category_id,
        image: project.image,
        status: project.status as 'completed' | 'in-progress' | 'planned',
        link: project.link,
        start_date: project.start_date,
        end_date: project.end_date,
        sort_order: project.sort_order || 0,
        is_featured: project.is_featured || false,
        created_at: project.created_at,
        updated_at: project.updated_at,
        gallery: Array.isArray(project.gallery) ? project.gallery : [],
        tags: Array.isArray(project.tags) ? project.tags : [],
        features: Array.isArray(project.features) ? project.features : [],
        tech_stack: Array.isArray(project.tech_stack) ? project.tech_stack : [],
        metrics: typeof project.metrics === 'object' && project.metrics ? project.metrics : {},
        title: trans?.title || project.id,
        subtitle: trans?.subtitle || null,
        description: trans?.description || '',
        content: trans?.content || null
      } as Project
    },
    enabled: !!projectId
  })
}

export function useCategory(categoryId: string) {
  const { i18n } = useTranslation()
  const language = (i18n.language as Language) || 'zh'
  
  return useQuery({
    queryKey: ['category', categoryId, language],
    queryFn: async () => {
      // 获取分类
      const { data: category, error: catError } = await supabase
        .from('project_categories')
        .select('*')
        .eq('id', categoryId)
        .maybeSingle()
      
      if (catError) throw catError
      if (!category) return null
      
      // 获取翻译
      const { data: trans, error: transError } = await supabase
        .from('category_translations')
        .select('*')
        .eq('category_id', categoryId)
        .eq('language', language)
        .maybeSingle()
      
      if (transError) throw transError
      
      return {
        id: category.id,
        icon: category.icon,
        sort_order: category.sort_order || 0,
        title: trans?.title || category.title,
        description: trans?.description || category.description
      } as ProjectCategory
    },
    enabled: !!categoryId
  })
}
